import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Search,
  Megaphone,
  Users,
  FileText,
  LogOut,
  Home as HomeIcon,
  Flame,
  Sparkles,
  Settings as SettingsIcon,
  ShieldCheck,
  Building2,
  Mail,
  Target,
  UsersRound,
  MapPin,
  Send,
  DatabaseZap,
  Handshake,
  PhoneCall,
  Radar,
  Fingerprint,
  Kanban,
  Wrench,
  Radio,
  Inbox,
  MessageSquare,
  ListChecks,
  BarChart3,
  Archive,
  Pencil,
  Eye,
  EyeOff,
  GripVertical,
  RotateCcw,
  Check,
  Plus,
} from "lucide-react";

export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  // "Analyze Address" — dedicated nav slot for the Property Lookup
  // flow (2026-02-17 owner ask: moved off the Dashboard's CTA button
  // into its own left-nav tab so reps can jump straight there).
  { to: "/lookup", label: "Analyze Address", icon: Search, id: "nav-analyze" },
  { to: "/boards", label: "CRM Pipeline", icon: Kanban, id: "nav-boards" },
  // "+ Create New Lead" — quick manual entry that starts with Property
  // Lookup and only asks the rep for what they learn from talking to
  // the seller (owner ask 2026-08-27). Visually prominent slot right
  // under CRM Pipeline.
  { to: "/leads/new", label: "+ Create New Lead", icon: Plus, id: "nav-create-lead", accent: "fuchsia" },
  // AI Lead Manager sits directly under CRM Pipeline and above Dialer —
  // this is the command center that turns Retell + AI SMS output into a
  // ranked "who to call next" queue. Badge shows count of unhandled
  // HOT/WARM leads (actionable, NOT just unread).
  { to: "/ai-lead-manager", label: "AI Lead Manager", icon: Flame, id: "nav-ai-lead-manager", badgeKey: "ai_lm_actionable" },
  // AI Calls Inbox — landing zone for Retell inbound calls whose caller
  // number isn't yet a CRM lead. Rep reviews AI-captured facts, then
  // converts to a full lead or dismisses. Sits right under AI Lead
  // Manager so the two AI-driven queues live next to each other.
  { to: "/ai-call-inbox", label: "AI Calls Inbox", icon: Sparkles, id: "nav-ai-call-inbox", badgeKey: "ai_call_inbox_new" },
  // Property Search hidden 2026-02-17 by owner. Uncomment (and drop
  // the `hidden` flag) to re-expose. Keeping the route + page code
  // intact so re-enabling is a one-line change.
  // { to: "/search", label: "Property Search", icon: Search, id: "nav-search", adminOnly: true },
  { to: "/dialer", label: "Dialer", icon: PhoneCall, id: "nav-dialer" },
  { to: "/dialer-performance", label: "Dialer Performance", icon: BarChart3, id: "nav-dialer-performance" },
  { to: "/admin/live-dialing", label: "Live Dialing", icon: Radio, id: "nav-live-dialing", adminOnly: true, end: true },
  { to: "/lists", label: "Call Lists", icon: ListChecks, id: "nav-lists" },
  { to: "/offers", label: "Offers", icon: FileText, id: "nav-offers" },
  { to: "/profit-calculator", label: "Profit Calculator", icon: FileText, id: "nav-profit-calc" },
  { to: "/reports", label: "Reporting", icon: BarChart3, id: "nav-reports" },
  { to: "/admin/marketing-leads", label: "Marketing Inbox", icon: Inbox, id: "nav-marketing-inbox", adminOnly: true, end: true },
  { to: "/communications", label: "Communications", icon: Mail, id: "nav-communications" },
  { to: "/sms-inbox", label: "SMS Inbox", icon: MessageSquare, id: "nav-sms-inbox", badgeKey: "sms_unread" },
  { to: "/sms-blaster", label: "SMS Blaster", icon: Send, id: "nav-sms-blaster", cap: "smsblaster.send" },
  { to: "/admin/access-requests", label: "Access Requests", icon: ShieldCheck, id: "nav-access-requests", adminOnly: true, end: true },
  { to: "/d4d", label: "Driving for $", icon: MapPin, id: "nav-d4d" },
  { to: "/repair-estimator", label: "Repair Estimator", icon: Wrench, id: "nav-repair" },
  { to: "/buyers", label: "Buyer List", icon: Users, id: "nav-buyers" },
  { to: "/team", label: "Team", icon: UsersRound, id: "nav-team" },
  // Rep Activity — real-time rep status + payroll calling-time report.
  // Managers audit exactly when reps are dialing vs idle vs on break,
  // click a rep for their daily/weekly timeline + PAYROLL CALLING TIME.
  { to: "/rep-activity", label: "Rep Activity", icon: UsersRound, id: "nav-rep-activity" },
  { to: "/call-monitor", label: "Call Monitor", icon: UsersRound, id: "nav-call-monitor" },
  // Archived Leads — read-only view of soft-deleted leads with every
  // Retell recording + SMS still attached. Restore or purge from here.
  { to: "/archived-leads", label: "Archived Leads", icon: Archive, id: "nav-archived-leads" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, id: "nav-settings", cap: "settings.write" },
];

const ADMIN_NAV = {
  to: "/admin",
  label: "Admin Console",
  icon: ShieldCheck,
  id: "nav-admin",
  end: true,   // only match exactly /admin — prevents /admin/marketing-leads from lighting this up
};

/**
 * Renders the nav interior (brand + links + sign-out). Used by:
 *  - Sidebar.jsx  (desktop, permanent)
 *  - MobileNav.jsx (drawer)
 */
export default function SidebarContent({ onNavigate }) {
  const { user, logout, can, capsLoading } = useAuth();
  const navigate = useNavigate();

  // Badges — SMS unread + AI Lead Manager actionable (HOT/WARM leads
  // with AI activity in the last 24h). Polls every 30s.
  const [badges, setBadges] = useState({ sms_unread: 0, ai_lm_actionable: 0, ai_call_inbox_new: 0 });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/sms/unread-count");
        if (alive) setBadges((b) => ({ ...b, sms_unread: data.unread || 0 }));
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get("/ai-lead-manager/counts");
        if (alive) setBadges((b) => ({ ...b, ai_lm_actionable: data.actionable || 0 }));
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get("/ai-call-inbox/count");
        if (alive) setBadges((b) => ({ ...b, ai_call_inbox_new: data.new || 0 }));
      } catch (_) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // ─── Admin-editable nav (2026-02-17, updated 2026-08-27 tenant-wide) ──
  // Admins can reorder, rename, or hide sidebar entries. Persisted to
  // the backend on `tenant_settings` so EVERY teammate in the tenant
  // sees the same layout the admin published (owner ask: "If I change
  // remove or add anything on admin side it must be apply across all
  // the employee"). LocalStorage is kept only as an instant-render
  // cache — the backend GET wins on load.
  const NAV_PREFS_KEY = user?.id ? `sidebar.navPrefs.${user.id}` : null;
  const [editMode, setEditMode] = useState(false);
  const [navPrefs, setNavPrefs] = useState({}); // { [id]: { order, label, hidden } }
  const dragId = useRef(null);

  // Load prefs on mount — tenant-shared first (backend), then fall
  // back to the local cache if the network hiccups.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tenant/nav-prefs");
        if (alive) setNavPrefs(data?.nav_prefs || {});
      } catch (_) {
        if (!NAV_PREFS_KEY) return;
        try {
          const raw = localStorage.getItem(NAV_PREFS_KEY);
          if (alive) setNavPrefs(raw ? JSON.parse(raw) : {});
        } catch (__) { if (alive) setNavPrefs({}); }
      }
    })();
    return () => { alive = false; };
  }, [NAV_PREFS_KEY]);

  const savePrefs = (next) => {
    setNavPrefs(next);
    // Local cache for instant re-render on refresh.
    if (NAV_PREFS_KEY) {
      try { localStorage.setItem(NAV_PREFS_KEY, JSON.stringify(next)); }
      catch (_) { /* quota / private mode — silent */ }
    }
    // Publish to the tenant so every teammate picks up the change.
    // Employees hitting the PUT will 403 — that's fine, they only READ.
    if (user?.is_admin) {
      api.put("/tenant/nav-prefs", { nav_prefs: next })
        .catch(() => { /* soft-fail — local UI already updated */ });
    }
  };

  const resetPrefs = () => {
    if (!confirm("Reset the sidebar to defaults? Your renamed / reordered / hidden items will come back for everyone in your team.")) return;
    if (NAV_PREFS_KEY) { try { localStorage.removeItem(NAV_PREFS_KEY); } catch (_) { /* ignore */ } }
    setNavPrefs({});
    if (user?.is_admin) {
      api.put("/tenant/nav-prefs", { nav_prefs: {} })
        .catch(() => { /* soft-fail */ });
    }
  };

  // Compose the rendered NAV: apply prefs (order + label + hidden),
  // then filter by admin/capability rules. Kept in a single memo-ish
  // pass so both the read view and the edit view see identical data.
  const renderedNav = (() => {
    const withPrefs = NAV.map((n, i) => {
      const p = navPrefs[n.id] || {};
      return {
        ...n,
        _order:  p.order ?? i,
        _label:  p.label && p.label.trim() ? p.label : n.label,
        _hidden: !!p.hidden,
      };
    }).sort((a, b) => a._order - b._order);
    return withPrefs.filter((n) => {
      if (n.adminOnly && !user?.is_admin) return false;
      if (n.cap && (capsLoading || !can(n.cap))) return false;
      return true;
    });
  })();

  // Drag-reorder helpers (native HTML5). We only need to know the
  // "which id am I dragging" + "which id am I over"; on drop we
  // recompute the numeric `order` for every visible row so gaps stay
  // clean.
  const onDragStart = (id) => (e) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_) { /* Safari */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (overId) => (e) => {
    e.preventDefault();
    const dropped = dragId.current;
    dragId.current = null;
    if (!dropped || dropped === overId) return;
    // Rebuild order sequentially from the current rendered order,
    // inserting `dropped` immediately BEFORE `overId`.
    const ids = renderedNav.map((n) => n.id).filter((id) => id !== dropped);
    const idx = ids.indexOf(overId);
    ids.splice(idx < 0 ? ids.length : idx, 0, dropped);
    const next = { ...navPrefs };
    ids.forEach((id, i) => {
      next[id] = { ...(next[id] || {}), order: i };
    });
    savePrefs(next);
  };

  const toggleHidden = (id) => {
    savePrefs({
      ...navPrefs,
      [id]: { ...(navPrefs[id] || {}), hidden: !navPrefs[id]?.hidden },
    });
  };

  const renameNav = (id, label) => {
    savePrefs({
      ...navPrefs,
      [id]: { ...(navPrefs[id] || {}), label },
    });
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-5 py-6 border-b border-[var(--app-line)]">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-2.5"
          data-testid="sidebar-brand"
        >
          <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-[var(--app-black)] text-white">
            <HomeIcon size={16} strokeWidth={2.4} />
          </span>
          <div>
            <div className="font-display text-lg leading-none">FlipPilot</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--app-text-muted)] mt-1.5">
              Acquisitions
            </div>
          </div>
        </Link>
      </div>

      {/* Admin-only edit-nav toolbar */}
      {user?.is_admin && (
        <div className="px-3 pt-2 flex items-center justify-between">
          <span className="text-[9px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
            Navigation
          </span>
          <div className="flex items-center gap-0.5">
            {editMode && (
              <button
                onClick={resetPrefs}
                className="p-1.5 rounded hover:bg-[var(--app-surface)] text-[var(--app-text-muted)]"
                title="Reset to defaults"
                data-testid="sidebar-reset"
              >
                <RotateCcw size={12} />
              </button>
            )}
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`p-1.5 rounded ${
                editMode
                  ? "bg-emerald-100 text-emerald-800"
                  : "hover:bg-[var(--app-surface)] text-[var(--app-text-muted)]"
              }`}
              title={editMode ? "Done editing" : "Edit sidebar (reorder / rename / hide)"}
              data-testid="sidebar-edit-toggle"
            >
              {editMode ? <Check size={12} /> : <Pencil size={12} />}
            </button>
          </div>
        </div>
      )}

      <nav
        className="flex-1 p-3 space-y-1 overflow-y-auto"
        data-testid="sidebar-nav"
      >
        {renderedNav.map((n) => {
          // In view mode we skip hidden items entirely. In edit mode
          // we still render them (dimmed) so admins can un-hide.
          if (n._hidden && !editMode) return null;
          const Icon = n.icon;
          const badgeCount = n.badgeKey ? badges[n.badgeKey] || 0 : 0;

          if (editMode) {
            return (
              <div
                key={n.id}
                draggable
                onDragStart={onDragStart(n.id)}
                onDragOver={onDragOver}
                onDrop={onDrop(n.id)}
                data-testid={`sidebar-edit-row-${n.id}`}
                className={`flex items-center gap-1.5 px-1.5 py-1 rounded-lg text-sm border border-transparent hover:border-[var(--app-line)] hover:bg-[var(--app-surface)] ${
                  n._hidden ? "opacity-45" : ""
                }`}
              >
                <GripVertical size={12} className="text-slate-400 cursor-grab shrink-0" />
                <Icon size={14} strokeWidth={2} className="shrink-0" />
                <input
                  type="text"
                  value={n._label}
                  onChange={(e) => renameNav(n.id, e.target.value)}
                  className="flex-1 min-w-0 bg-transparent border-b border-transparent focus:border-[var(--app-line)] outline-none text-sm py-0.5"
                  data-testid={`sidebar-rename-${n.id}`}
                />
                <button
                  onClick={() => toggleHidden(n.id)}
                  className="p-1 rounded hover:bg-white text-slate-500 shrink-0"
                  title={n._hidden ? "Show" : "Hide"}
                  data-testid={`sidebar-hide-${n.id}`}
                >
                  {n._hidden ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>
            );
          }

          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              onClick={onNavigate}
              className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
            >
              <Icon size={16} strokeWidth={2} />
              <span>{n._label}</span>
              {badgeCount > 0 && (
                <span className="ml-auto min-w-[18px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center"
                      data-testid={`${n.id}-badge`}>
                  {badgeCount > 99 ? "99+" : badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
        {user?.is_admin && !editMode && (
          <NavLink
            key={ADMIN_NAV.to}
            to={ADMIN_NAV.to}
            end={ADMIN_NAV.end}
            data-testid={ADMIN_NAV.id}
            onClick={onNavigate}
            className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
          >
            <ADMIN_NAV.icon size={16} strokeWidth={2} />
            <span>{ADMIN_NAV.label}</span>
          </NavLink>
        )}
      </nav>

      <div className="p-3 border-t border-[var(--app-line)]">
        <div className="px-3 py-2 mb-2 rounded-xl bg-[var(--app-surface)]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--app-text-muted)]">
            Signed in as
          </div>
          <div
            className="text-sm font-medium truncate mt-0.5"
            data-testid="sidebar-user"
          >
            {user?.email}
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="side-link w-full"
        >
          <LogOut size={16} />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}
